import { isEmpty } from "lodash";
import { Fragment, useEffect, useRef } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { CircularProgress } from "@/Components/v2/atoms/Progress/CircularProgress";
import { Stack } from "@/Components/v2/atoms/Stack";
import NoData from "@/Components/v2/molecule/NoData";
import Header from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useConfig } from "@/hooks/useConfig";
import { Toggle, ToolBarShared } from "@/shared/components/DataGrid/ToolBarShared";
import DataGridContainer from "@/shared/components/DataGridContainer";
import { useDateRangeStore } from "@/shared/components/date-range/store/useDateRangeStore";
import Summary from "@/shared/components/Sections/Summary";
import Title from "@/shared/components/Sections/Title";
import { useMetricOptions } from "@/shared/hooks/useMetricOptions";
import { BusinessUnitToggle, GeoViewToggle, MetricType, ViewToggle } from "@/shared/types";
import { Layer } from "@/Utils/hierarchy";

import { useFormatsROIs } from "./api/getFormatROIs";
import BreakdownV2 from "./components/Sections/BreakdownV2";
import { content, noDataContent } from "./constants/content";
import { DEFAULT_COLUMNS } from "./constants/defaults";
import { getColumnDefinitions } from "./getColumnDefinitions";
import { useFormatsStore } from "./store/useFormatsStore";
import { useAvailableFilters } from "./useAvailableFilters";

interface FormatsProps {}

const Formats: React.FC<FormatsProps> = () => {
  const { config } = useConfig();

  const { startDate, endDate } = useDateRangeStore();
  const { formats, channels, targetNode, geographies } = useFormatsStore((state) => state.filters);
  const {
    setTargetNode,
    setActiveColumns,
    setGridView,
    setGeoView,
    setBusinessUnitToggle,
    setMetricType,
  } = useFormatsStore();
  const { geoView, gridView, activeColumns, businessUnitToggle, metricType } = useFormatsStore(
    (state) => state.grid
  );
  const { chartView } = useFormatsStore((state) => state.chart);
  const { setFormats, setChannels, setChartView } = useFormatsStore();
  const metricOptions = useMetricOptions();

  const apiRef = useRef(null);

  const currencySymbol = config.currency.getSymbol();
  const formatCurrency = config.currency.formatCurrency;

  const { loading: areGeosLoading, initInfo: info, nodes, loading } = useInfo();
  useEffect(() => {
    if (nodes && !loading && targetNode === null) {
      const defaultNodeUUID = nodes?.getDefaultNode()?.node.uuid;
      if (defaultNodeUUID) {
        setTargetNode(defaultNodeUUID);
      }
    }
  }, [loading, targetNode, nodes, setTargetNode]);

  const selectedNode = nodes?.getNode(targetNode || "");

  const { data: formatsROIsV3, isLoading: isFormatsLoading } = useFormatsROIs({
    brand: selectedNode?.node.brand ?? "default",
    category: selectedNode?.node.category ?? "default",
    product: selectedNode?.node.product ?? "default",
    channels: channels,
    startDate: startDate ?? "",
    endDate: endDate ?? "",
    graphView: chartView,
    tableView: gridView,
    geoViewToggle: geoView,
    businessUnitToggle: businessUnitToggle,
    formats: formats,
    geographies: geographies,
    config: {
      enabled:
        !!selectedNode &&
        !!startDate &&
        !!endDate &&
        !!chartView &&
        !!gridView &&
        !!businessUnitToggle,
    },
  });

  const columns = getColumnDefinitions(activeColumns, {
    currencySymbol,
    formatCurrency,
    geoView,
    gridView,
    businessUnitToggle,
    metricType,
    config,
  });

  const availableFilters = useAvailableFilters({
    selectedNode,
    startDate: startDate ?? "",
    endDate: endDate ?? "",
  });

  const { moduleOpened } = useAnalytics();

  useEffect(() => {
    moduleOpened("formats", Layer.Portfolio);
  }, []);

  const showWelcomeScreen = !formats || formats.length === 0 || channels.length === 0;

  const isLoading = !nodes || isFormatsLoading || areGeosLoading;

  const toggles: Toggle[] = [
    {
      id: "viewToggle",
      selectedValue: gridView,
      options: [
        { label: "Formats", value: ViewToggle.INSIGHT },
        { label: "Channels", value: ViewToggle.CHANNEL },
      ],
    },
    {
      id: "businessUnitToggle",
      selectedValue: businessUnitToggle,
      options: [
        { label: "Aggregate Business Units", value: "aggregate" },
        { label: "Split by Business Unit", value: "split" },
      ],
    },
  ];

  const metricDropdownConfig = {
    label: "Show",
    value: metricType,
    options: metricOptions,
    onChange: (value: string | null) => {
      if (value) {
        setMetricType(value as MetricType);
      }
    },
  };

  const [geoData] = info?.geographies || [];

  if (geoData && geoData.length > 0) {
    toggles.push({
      id: "geoViewToggle",
      selectedValue: geoView,
      options: [
        { label: "Aggregate Geographies", value: GeoViewToggle.AGGREGATE },
        { label: "Split by Geography", value: GeoViewToggle.SPLIT },
      ],
    });
  }

  return (
    <Box>
      <Title title={content.title} subtitle={content.subtitle} />
      <Header availableFilters={availableFilters} />
      {isLoading ? (
        <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
          <CircularProgress size={150} />
        </Stack>
      ) : isEmpty(formatsROIsV3?.chartView) || isEmpty(formatsROIsV3?.totals) ? (
        <NoData title={noDataContent.title} description={noDataContent.description} />
      ) : (
        <Fragment>
          <Box sx={{ marginX: 4 }}>
            <Summary
              spend={formatsROIsV3?.totals.totalSpend ?? 0}
              revenue={formatsROIsV3?.totals.totalRevenue ?? 0}
              roi={formatsROIsV3?.totals.totalRoi ?? 0}
              formatCurrency={formatCurrency}
            />
            <BreakdownV2
              title={content.title}
              showWelcomeScreen={showWelcomeScreen}
              data={formatsROIsV3?.chartView ?? []}
              totalSpend={formatsROIsV3?.totals.totalSpend ?? 0}
              totalRevenue={formatsROIsV3?.totals.totalRevenue ?? 0}
              currencySymbol={currencySymbol}
              setChannels={setChannels}
              setFormats={setFormats}
              setChartView={setChartView}
            />
            <DataGridContainer
              apiRef={apiRef}
              rows={formatsROIsV3?.tableView ?? []}
              columns={columns}
              metricDropdown={metricDropdownConfig}
              toolbarComponent={
                <ToolBarShared
                  apiRef={apiRef}
                  columns={columns}
                  activeColumns={activeColumns}
                  toggles={toggles}
                  handleApplyFilters={({ activeColumns, toggles }) => {
                    const gridViewToggle = toggles["viewToggle"];
                    const geoViewToggle = toggles["geoViewToggle"];
                    const businessUnitToggle = toggles["businessUnitToggle"];
                    setActiveColumns(activeColumns);
                    setGridView(gridViewToggle as ViewToggle);

                    if (geoViewToggle) {
                      setGeoView(geoViewToggle as GeoViewToggle);
                    }

                    if (businessUnitToggle) {
                      setBusinessUnitToggle(businessUnitToggle as BusinessUnitToggle);
                    }
                  }}
                  handleResetFilters={() => {
                    setActiveColumns(DEFAULT_COLUMNS);
                    setGridView(ViewToggle.INSIGHT);
                    setGeoView(GeoViewToggle.AGGREGATE);
                    setMetricType(MetricType.MARKETING_ROI);
                    setBusinessUnitToggle(BusinessUnitToggle.AGGREGATE);
                  }}
                />
              }
              options={{
                rowHeight: 52,
                sortModel: [{ field: "total_spend", sort: "desc" }],
              }}
            />
          </Box>
        </Fragment>
      )}
    </Box>
  );
};

export default Formats;
